import { Button, Col, Row, Card, Typography, Form } from "antd"
import { PlusOutlined, DeleteOutlined, CopyOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { observer } from "mobx-react"
import { SectionQuestion, SectionQuestionType } from "../../../../domain/entities/SectionQuestion"
import { SectionQuestionOption } from "../../../../domain/entities/SectionQuestionOption"
import { InputType } from "../../../components/TextInput/const/InputType.enum"
import { FormField } from "../../../components/Form/FormField"
import { SurveySectionFormData } from "../SurveysViewModel"

const { Title, Text } = Typography

interface SurveySectionQuestionsFormProps {
	formData: SurveySectionFormData
	onChange: (formData: Partial<SurveySectionFormData>) => void
}

const SectionQuestionsForm = ({ formData, onChange }: SurveySectionQuestionsFormProps) => {
	const { t } = useTranslation("surveys", { keyPrefix: "form.sections" })

	const addQuestion = () => {
		const newQuestion: SectionQuestion = {
			sectionId: formData.id,
			text: "",
			text_en: "",
			text_ca: "",
			type: SectionQuestionType.TEXT,
			order: 0,
			required: false
		}
		onChange({ questions: (formData.questions ?? []).concat([newQuestion]) })
	}

	const updateQuestion = (questionIndex: number, question: Partial<SectionQuestion>) => {
		const newQuestions = [...(formData.questions ?? [])]
		newQuestions[questionIndex] = { ...newQuestions[questionIndex], ...question }
		onChange({ questions: newQuestions })
	}

	const removeQuestion = (questionIndex: number) => {
		onChange({ questions: formData.questions?.filter((_, index) => index !== questionIndex) })
	}

	const duplicateQuestion = (questionIndex: number) => {
		const currentQuestion = formData.questions![questionIndex]
		const duplicatedQuestion: SectionQuestion = {
			...currentQuestion,
			id: undefined,
			options: currentQuestion.options ? currentQuestion.options.map(o => ({ ...o, id: undefined })) : []
		}
		const newQuestions = [...formData.questions!, duplicatedQuestion]
		onChange({ questions: newQuestions })
	}

	const addOption = (questionIndex: number) => {
		const newOption: SectionQuestionOption = {
			text: "",
			text_en: "",
			text_ca: "",
			order: 0
		}
		const newQuestions = [...(formData.questions ?? [])]
		newQuestions[questionIndex] = {
			...newQuestions[questionIndex],
			options: (newQuestions[questionIndex].options ?? []).concat([newOption])
		}
		onChange({ questions: newQuestions })
	}

	const updateOption = (questionIndex: number, optionIndex: number, option: Partial<SectionQuestionOption>) => {
		const newQuestions = [...(formData.questions ?? [])]
		newQuestions[questionIndex] = {
			...newQuestions[questionIndex],
			options: newQuestions[questionIndex].options?.map((o, i) => (i === optionIndex ? { ...o, ...option } : o))
		}
		onChange({ questions: newQuestions })
	}

	const removeOption = (questionIndex: number, optionIndex: number) => {
		const newQuestions = [...(formData.questions ?? [])]
		newQuestions[questionIndex] = {
			...newQuestions[questionIndex],
			options: newQuestions[questionIndex].options?.filter((_, index) => index !== optionIndex)
		}
		onChange({ questions: newQuestions })
	}

	return (
		<div className="px-4">
			<div className="flex justify-between items-center mb-4">
				<Title level={5} style={{ margin: 0 }}>
					{t("questions")}
				</Title>
				<Button type="primary" icon={<PlusOutlined />} onClick={addQuestion} size="small">
					{t("addQuestion")}
				</Button>
			</div>

			{formData.questions?.map((question, questionIndex) => (
				<Card
					key={questionIndex}
					className="mb-4"
					size="small"
					title={
						<div className="flex justify-between items-center">
							<Text strong>
								{t("question")} {questionIndex + 1}
							</Text>
							<div className="flex gap-2">
								<Button
									type="text"
									icon={<CopyOutlined />}
									onClick={() => duplicateQuestion(questionIndex)}
									size="small"
								/>
								<Button
									type="text"
									icon={<DeleteOutlined />}
									onClick={() => removeQuestion(questionIndex)}
									danger
									size="small"
								/>
							</div>
						</div>
					}
				>
					<Row gutter={[16, 0]} className="px-4">
						<Col span={12}>
							<FormField.Input
								label={t("questionText")}
								name={["questions", questionIndex, "text"]}
								mandatory
								placeHolder={t("questionTextPlaceholder")}
								onChange={(text: string) => updateQuestion(questionIndex, { text })}
							/>
							<FormField.Input
								label={t("questionTextEN")}
								name={["questions", questionIndex, "text_en"]}
								mandatory
								placeHolder={t("questionTextPlaceholderEN")}
								onChange={(text_en: string) => updateQuestion(questionIndex, { text_en })}
							/>
							<FormField.Input
								label={t("questionTextCA")}
								name={["questions", questionIndex, "text_ca"]}
								mandatory
								placeHolder={t("questionTextPlaceholderCA")}
								onChange={(text_ca: string) => updateQuestion(questionIndex, { text_ca })}
							/>
						</Col>
						<Col span={6}>
							<FormField.Select
								label={t("questionType")}
								name={["questions", questionIndex, "type"]}
								mandatory
								placeholder={t("questionTypePlaceholder")}
								onChange={(type: SectionQuestionType) =>
									updateQuestion(questionIndex, { type, options: [] })
								}
								options={[
									{ value: SectionQuestionType.TEXT, label: t("textType") },
									{ value: SectionQuestionType.OPTION, label: t("optionType") }
								]}
							/>
						</Col>
						<Col span={6}>
							<FormField.Input
								label={t("questionOrder")}
								name={["questions", questionIndex, "order"]}
								inputType={InputType.NUMBER}
								placeHolder={t("questionOrderPlaceholder")}
								onChange={(order: string) =>
									updateQuestion(questionIndex, { order: parseInt(order) || 0 })
								}
							/>
						</Col>
					</Row>

					{/* Options */}
					{question.type === SectionQuestionType.OPTION && (
						<div className="mt-4 px-4">
							<div className="flex justify-between items-center mb-4">
								<Text strong>{t("options")}</Text>
								<Button
									type="primary"
									icon={<PlusOutlined />}
									onClick={() => addOption(questionIndex)}
									size="small"
								>
									{t("addOption")}
								</Button>
							</div>

							{question.options?.map((option, optionIndex) => (
								<Card
									key={optionIndex}
									size="small"
									className="mb-2"
									title={
										<div className="flex justify-between items-center">
											<Text strong>
												{t("option")} {optionIndex + 1}
											</Text>
											<Button
												type="text"
												icon={<DeleteOutlined />}
												onClick={() => removeOption(questionIndex, optionIndex)}
												danger
												size="small"
											/>
										</div>
									}
								>
									<Row gutter={[16, 0]} className="px-4">
										<Col span={12}>
											<FormField.Input
												label={t("optionText")}
												name={["questions", questionIndex, "options", optionIndex, "text"]}
												mandatory
												placeHolder={t("optionTextPlaceholder")}
												onChange={(text: string) =>
													updateOption(questionIndex, optionIndex, { text })
												}
											/>
										</Col>
										<Col span={12}>
											<FormField.Input
												label={t("optionOrder")}
												name={["questions", questionIndex, "options", optionIndex, "order"]}
												inputType={InputType.NUMBER}
												placeHolder={t("optionOrderPlaceholder")}
												onChange={(order: string) =>
													updateOption(questionIndex, optionIndex, {
														order: parseInt(order) || 0
													})
												}
											/>
										</Col>
									</Row>
									<Row gutter={[16, 0]} className="px-4">
										<Col span={12}>
											<FormField.Input
												label={t("optionTextEN")}
												name={["questions", questionIndex, "options", optionIndex, "text_en"]}
												mandatory
												placeHolder={t("optionTextPlaceholderEN")}
												onChange={(text_en: string) =>
													updateOption(questionIndex, optionIndex, { text_en })
												}
											/>
										</Col>
									</Row>
									<Row gutter={[16, 0]} className="px-4">
										<Col span={12}>
											<FormField.Input
												label={t("optionTextCA")}
												name={["questions", questionIndex, "options", optionIndex, "text_ca"]}
												mandatory
												placeHolder={t("optionTextPlaceholderCA")}
												onChange={(text_ca: string) =>
													updateOption(questionIndex, optionIndex, { text_ca })
												}
											/>
										</Col>
									</Row>
								</Card>
							))}
						</div>
					)}
				</Card>
			))}
		</div>
	)
}

export default observer(SectionQuestionsForm)
